import { useCallback } from 'react';
import styled from 'styled-components';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import { PatientEntitlement } from '@dreryk/egabinet-patient-frontend-api';

import { FormController } from '@dreryk/react-components';

import { ConfirmingDocumentNumberTextInput } from '../confirmingDocument/ConfirmingDocumentNumberTextInput';
import {
   ConfirmingDocumentTypeSelect,
   OnConfirmingDocumentTypeSelectItemsChanged,
} from '../confirmingDocument/ConfirmingDocumentTypeSelect';
import { EntitlementDocumentDateFields } from '../confirmingDocument/EntitlementDocumentDateFields/EntitlementDocumentDateFields';
import { PregnancyDateFields } from '../confirmingDocument/PregnancyDateFields/PregnancyDateFields';

import {
   usePatientEntitlementUpdateCommand,
   usePatientEntitlementUpdateFormController,
} from './PatientEntitlementUpdateForm.hooks';
import {
   OnPatientEntitlementDocumentUpdated,
   PatientEntitlementUpdateFormRenderer,
} from './PatientEntitlementUpdateForm.types';

const StyledPatientEntitlementForm = styled.form`
   display: grid;
   grid-gap: 1em;
   grid-template-columns: 1fr 1fr;

   > [class*='Status'] {
      justify-content: flex-start;
      grid-column: 1/-1;
   }
`;

StyledPatientEntitlementForm.displayName = 'StyledPatientEntitlementForm';

type PatientEntitlementUpdateFormProps = {
   readonly entitlementType: EntitlementType;
   readonly patientEntitlement: PatientEntitlement;
   readonly onPatientEntitlementDocumentUpdated: OnPatientEntitlementDocumentUpdated;
   readonly children: PatientEntitlementUpdateFormRenderer;
};

export const PatientEntitlementUpdateForm = (props: PatientEntitlementUpdateFormProps): JSX.Element => {
   const { register, handleSubmit, setValue, formState, watch } = usePatientEntitlementUpdateFormController({
      patientEntitlement: props.patientEntitlement,
      entitlementType: props.entitlementType,
   });

   const { executeAsync: updatePatientEntitlement } = usePatientEntitlementUpdateCommand({
      entitlementType: props.entitlementType,
      onPatientEntitlementDocumentUpdated: props.onPatientEntitlementDocumentUpdated,
   });

   const handleConfirmingDocumentTypeSelectItemsChanged: OnConfirmingDocumentTypeSelectItemsChanged = useCallback(
      (items) => {
         const foundConfirmingDocumentType = items.find(
            (item) => item.name === props.patientEntitlement.entitlementConfirmationTypeName
         );

         setValue('confirmingDocumentType', foundConfirmingDocumentType ?? null, { shouldDirty: false });
      },
      [props.patientEntitlement.entitlementConfirmationTypeName, setValue]
   );

   const patientEntitlementUpdate = handleSubmit(updatePatientEntitlement);

   return (
      <StyledPatientEntitlementForm noValidate>
         <FormController controller={register('id')} />
         <ConfirmingDocumentTypeSelect
            register={register}
            entitlementType={props.entitlementType}
            onConfirmingDocumentTypeSelectItemsChanged={handleConfirmingDocumentTypeSelectItemsChanged}
         />
         <ConfirmingDocumentNumberTextInput register={register} />
         {props.entitlementType === 'C' ? (
            <PregnancyDateFields register={register} watch={watch} />
         ) : (
            <EntitlementDocumentDateFields entitlementType={props.entitlementType} register={register} />
         )}

         {props.children({ patientEntitlementUpdate: patientEntitlementUpdate, shouldConfirmExit: formState.isDirty })}
      </StyledPatientEntitlementForm>
   );
};
